import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Day } from "date-fns";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";
import { Icon } from "../Icon";
import { A11yText } from "../A11yText";
import { LpgDiscontinueOutline, LpgContinueOutline } from "../../icons";
import CalendarHeader from "./CalendarHeader";
import CalendarBody from "./CalendarBody";
import { DateRangeActionType, useDateRangeAction } from "./useDateRangeAction";

import styles from "./DateRangePicker.scss";

export type CalendarProps = {
  locale?: string;
  firstDayOfWeek?: Day;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  previewKeyboardDate?: Date;
  previewDate?: Date;
  selectedCalendarMonth: number;
  selectedCalendarYear: number;
};

const defaultProps = {
  locale: "de-DE",
  firstDayOfWeek: 1,
  selectedStartDate: undefined,
  selectedEndDate: undefined,
  previewKeyboardDate: undefined,
  previewDate: undefined,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  CalendarProps,
  typeof defaultProps
> = defaultProps;

const Calendar: ((props: CalendarProps) => JSX.Element) &
  ReactComponentStaticProps<CalendarProps> = ({
  locale = defaultProps.locale,
  firstDayOfWeek = defaultProps.firstDayOfWeek,
  selectedStartDate,
  selectedEndDate,
  previewKeyboardDate,
  previewDate,
  selectedCalendarMonth,
  selectedCalendarYear,
}) => {
  const dispatch = useDateRangeAction();
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    }
  }, []);

  const handlePrevByKeyboard = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        dispatch({
          type:
            DateRangeActionType.SelectPrevCalendarMonthWithPreviewDateUpdate,
        });
        break;
      default:
    }
  };

  const handleNextByKeyboard = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        dispatch({
          type:
            DateRangeActionType.SelectNextCalendarMonthWithPreviewDateUpdate,
        });
        break;
      default:
    }
  };

  return (
    <div className={styles["calendar-month-view"]}>
      <div
        className={styles["month-header"]}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          className={styles["month-change-button"]}
          onClick={() =>
            dispatch({
              type: DateRangeActionType.SelectPrevCalendarMonth,
            })
          }
          onKeyDown={handlePrevByKeyboard}
          type="button"
        >
          <A11yText>Vorherigen Kalendermonat anzeigen</A11yText>
          <Icon icon={LpgDiscontinueOutline} size={Icon.Size.parent} />
        </button>
        <span className={styles["month-label"]} aria-live="polite">
          {new Date(
            selectedCalendarYear,
            selectedCalendarMonth
          ).toLocaleDateString("de-DE", {
            year: "numeric",
            month: "long",
          })}
        </span>
        <button
          className={styles["month-change-button"]}
          onClick={() =>
            dispatch({
              type: DateRangeActionType.SelectNextCalendarMonth,
            })
          }
          onKeyDown={handleNextByKeyboard}
          type="button"
        >
          <A11yText>Nächsten Kalendermonat anzeigen</A11yText>
          <Icon icon={LpgContinueOutline} size={Icon.Size.parent} />
        </button>
      </div>

      <table role="grid" className={styles.calendar}>
        <CalendarHeader locale={locale} firstDayOfWeek={firstDayOfWeek} />
        <CalendarBody
          firstDayOfWeek={firstDayOfWeek}
          previewKeyboardDate={previewKeyboardDate}
          selectedCalendarMonth={selectedCalendarMonth}
          selectedCalendarYear={selectedCalendarYear}
          selectedEndDate={selectedEndDate}
          selectedStartDate={selectedStartDate}
          previewDate={previewDate}
          firstRender={firstRender.current}
        />
      </table>
    </div>
  );
};

Calendar.propTypes = {
  locale: PropTypes.string,
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  selectedStartDate: PropTypes.instanceOf(Date),
  selectedEndDate: PropTypes.instanceOf(Date),
  previewKeyboardDate: PropTypes.instanceOf(Date),
  previewDate: PropTypes.instanceOf(Date),
  selectedCalendarMonth: PropTypes.number.isRequired,
  selectedCalendarYear: PropTypes.number.isRequired,
};

Calendar.defaultProps = validatedDefaultProps;

Calendar.displayName = "DateRangePicker.Calendar";

export default Calendar;
